import { toast } from "sonner";
import { saveJob, formatApiError } from "@/lib/api";
import { Save, AlertTriangle, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShareButton } from "@/components/ShareButton";

export function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="mb-8">
      <div className="mb-4">
        <h2 className="heading text-xl font-bold text-[#f8fafc]">{title}</h2>
        {subtitle && <p className="text-sm text-[#94a3b8] mt-1 max-w-3xl">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export function CalcCard({ title, formula, children, warning, testid }) {
  return (
    <div data-testid={testid} className="border border-[#334155] bg-[#11151c] hover:border-[#f59e0b]/40 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-5 py-3 border-b border-[#334155]">
        <h3 className="heading text-sm font-bold tracking-wide uppercase">{title}</h3>
        {formula && <code className="mono text-[10px] sm:text-xs text-[#f59e0b] break-all sm:whitespace-nowrap">{formula}</code>}
      </div>
      <div className="p-5">{children}</div>
      {warning && (
        <div className="border-t border-[#ef4444]/40 bg-[#ef4444]/10 px-5 py-2.5 flex gap-2 items-start text-xs text-[#fca5a5]">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{warning}</span>
        </div>
      )}
    </div>
  );
}

export function NumField({ label, unit, value, onChange, step = "any", testid, hint }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs mono tracking-wider text-[#94a3b8] uppercase">{label}</span>
        {unit && <span className="text-[10px] mono text-[#64748b]">{unit}</span>}
      </div>
      <input
        data-testid={testid}
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0b0e13] border border-[#334155] text-[#f8fafc] px-3 py-2 mono text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-1 focus:ring-[#f59e0b]"
      />
      {hint && <div className="text-[10px] text-[#64748b] mt-1">{hint}</div>}
    </label>
  );
}

export function ResultRow({ label, value, unit, emphasis }) {
  return (
    <div
      className={`flex items-baseline justify-between py-2 border-b border-[#334155]/50 ${
        emphasis ? "text-[#f59e0b]" : ""
      }`}
    >
      <span className="text-xs mono uppercase tracking-wider text-[#94a3b8]">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className={`mono text-lg ${emphasis ? "font-bold text-[#fbbf24]" : "text-[#f8fafc]"}`}>
          {value ?? "—"}
        </span>
        {unit && <span className="text-[10px] mono text-[#64748b]">{unit}</span>}
      </div>
    </div>
  );
}

export function PrimaryButton({ children, onClick, testid, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold transition-colors disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#f8fafc] px-3 py-2 mono text-xs tracking-widest uppercase transition-colors"
    >
      {children}
    </button>
  );
}

export function SaveButton({ getPayload }) {
  const { isAuthed, hasPro, setShowAuth } = useAuth();
  const navigate = useNavigate();

  const onClick = async () => {
    if (!isAuthed) {
      toast.info("Sign in to save jobs");
      setShowAuth(true);
      return;
    }
    if (!hasPro) {
      toast.info("Pro plan required to save jobs");
      navigate("/pricing");
      return;
    }
    const p = getPayload();
    if (!p) {
      toast.error("Run a calculation first");
      return;
    }
    const name = window.prompt("Name this calculation:", p.default_name || "Job");
    if (!name) return;
    try {
      await saveJob({ name, category: p.category, calculator: p.calculator, inputs: p.inputs, outputs: p.outputs, units: p.units });
      toast.success("Saved to job history");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Pro plan required — redirecting to pricing");
        navigate("/pricing");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
      }
    }
  };

  return (
    <div className="inline-flex gap-2">
    <ShareButton getPayload={getPayload} />
    <button
      data-testid="save-calc-btn"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors"
    >
      {hasPro ? <Save className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />} Save
    </button>
    </div>
  );
}
